/* <i80-drawer>: an i80 component of our own (not in Helios), built only from i80 tokens.
   A vertical panel that folds down in the page flow — the inline counterpart to the flyout.

   The look: a calm surface that slides open, a quiet tertiary-styled toggle with a chevron
   that turns, and soft grabbable item cards that lift a touch under the pointer. Nothing
   shouts; the motion carries the meaning. It stays content-agnostic — the cards are whatever
   the author drops in; this file only dresses the frame, the items and the dropzone. */
.i80-drawer {
  position: relative;
  display: block;
  color: var(--i80-color-foreground-primary);
  font-family: var(--i80-typography-font-stack-text);
}

/* --- the toggle (only when the drawer carries a label) --- */
.i80-drawer__toggle {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  padding: 6px 10px 6px 6px;
  color: var(--i80-color-foreground-action);
  font: inherit;
  font-weight: var(--i80-typography-font-weight-medium);
  font-size: var(--i80-typography-body-200-font-size);
  line-height: var(--i80-typography-body-200-line-height);
  background: transparent;
  border: 0;
  border-radius: var(--i80-border-radius-medium);
  cursor: pointer;
}
.i80-drawer__toggle[hidden] { display: none; }
.i80-drawer__toggle:hover { color: var(--i80-color-foreground-action-hover); background: var(--i80-color-surface-faint); }
.i80-drawer__toggle:focus-visible { outline: 3px solid var(--i80-color-focus-action-external); outline-offset: 1px; }

.i80-drawer__toggle-icon {
  display: inline-flex;
  transition: transform 180ms ease;
}
.i80-drawer--open .i80-drawer__toggle-icon { transform: rotate(180deg); }
.i80-drawer__toggle-icon .i80-icon { width: 16px; height: 16px; }

/* --- push: the wrapper grows from 0fr to 1fr, so the panel takes room and shoves the
   content below it down. The panel clips its overflow, which is what the row trick needs. --- */
.i80-drawer--push .i80-drawer__wrapper {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 220ms ease;
}
.i80-drawer--push.i80-drawer--open .i80-drawer__wrapper {
  grid-template-rows: 1fr;
}
.i80-drawer--push .i80-drawer__panel {
  min-height: 0;
  overflow: hidden;
}

/* --- float: the wrapper is lifted out of the flow and laid over the content below,
   anchored just under the toggle (or the drawer's top when there is no toggle). --- */
.i80-drawer--float .i80-drawer__wrapper {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 20;
  min-width: 100%;
  opacity: 0;
  transform: translateY(-6px);
  visibility: hidden;
  transition: opacity 180ms ease, transform 180ms ease, visibility 0s linear 180ms;
}
.i80-drawer--float.i80-drawer--open .i80-drawer__wrapper {
  opacity: 1;
  transform: none;
  visibility: visible;
  transition: opacity 180ms ease, transform 180ms ease;
}
.i80-drawer--float .i80-drawer__panel {
  background: var(--i80-color-surface-primary);
  border: 1px solid var(--i80-color-border-primary);
  border-radius: var(--i80-border-radius-large);
  box-shadow: var(--i80-elevation-overlay-box-shadow);
}

@media (prefers-reduced-motion: reduce) {
  .i80-drawer__wrapper,
  .i80-drawer__toggle-icon { transition: none; }
}

/* --- the panel body: where the author's items (and anything else) live --- */
.i80-drawer--labelled .i80-drawer__panel { margin-top: 6px; }

.i80-drawer__items {
  display: flex;
  flex-flow: row wrap;
  gap: 8px;
  padding: 12px;
  background: var(--i80-color-surface-faint);
  border-radius: var(--i80-border-radius-large);
}
.i80-drawer--float .i80-drawer__items { background: transparent; }

/* --- the item: a soft grabbable card, the flow block's calmer cousin --- */
.i80-drawer__item {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  box-sizing: border-box;
  padding: 10px 14px;
  color: var(--i80-color-foreground-strong);
  font: inherit;
  font-size: var(--i80-typography-body-200-font-size);
  line-height: 20px;
  background: var(--i80-color-surface-primary);
  border: 1px solid var(--i80-color-border-primary);
  border-radius: 12px;
  box-shadow: var(--i80-elevation-low-box-shadow);
  cursor: grab;
  touch-action: none;
  user-select: none;
  transition: box-shadow 140ms ease, transform 140ms ease;
}
.i80-drawer__item:hover {
  box-shadow: var(--i80-elevation-mid-box-shadow);
  transform: translateY(-1px);
}
.i80-drawer__item:focus-visible {
  outline: 3px solid var(--i80-color-focus-action-external);
  outline-offset: 2px;
}
.i80-drawer__item--dragging {
  cursor: grabbing;
  opacity: 0.4;
  box-shadow: none;
  transform: none;
}
.i80-drawer__item--disabled {
  color: var(--i80-color-foreground-disabled);
  cursor: not-allowed;
  box-shadow: none;
}
.i80-drawer__item--disabled:hover { transform: none; box-shadow: none; }

@media (prefers-reduced-motion: reduce) {
  .i80-drawer__item { transition: none; }
}

/* The card that follows the pointer during a drag: a copy, pinned to the viewport. */
.i80-drawer__ghost {
  position: fixed;
  z-index: 1000;
  pointer-events: none;
  opacity: 0.92;
  box-shadow: var(--i80-elevation-high-box-shadow);
  transform: rotate(-1deg);
}

/* --- the dropzone: the bundled landing field, equally content-agnostic --- */
.i80-dropzone {
  display: flex;
  flex-flow: row wrap;
  gap: 8px;
  box-sizing: border-box;
  min-height: 64px;
  padding: 12px;
  background: var(--i80-color-surface-primary);
  border: 1px dashed var(--i80-color-border-primary);
  border-radius: var(--i80-border-radius-large);
  transition: border-color 140ms ease, background 140ms ease;
}
.i80-dropzone--empty {
  align-items: center;
  justify-content: center;
}
.i80-dropzone--over {
  background: var(--i80-color-surface-action);
  border-color: var(--i80-color-border-action);
  border-style: solid;
}
.i80-dropzone__empty {
  margin: 0;
  color: var(--i80-color-foreground-faint);
  font-size: var(--i80-typography-body-100-font-size);
  line-height: var(--i80-typography-body-100-line-height);
  text-align: center;
}
@media (prefers-reduced-motion: reduce) {
  .i80-dropzone { transition: none; }
}
